import { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { abnjSso } from '../sso';
import * as itsSignIn from '../sso/its-sign-in';

/**
 * AuthContext: who is signed in, for the whole site.
 *
 * Provides (via useAuth()):
 *   isAuthenticated  true once the auth worker confirms the abnj_sso cookie
 *   authChecked      false until the first session check finishes (pages wait for it)
 *   user             the member's ITS profile: { itsId, name, gender, age, jamaat, jamaatInitials, jamiaat, extra }
 *   verifyToken      sign-in page only: hand ITS's Token + DT to the auth worker
 *   login            sign-in page only: go to ITS OneLogin
 *
 * How:
 *   - On page load, asks the auth worker once who is signed in (abnjSso.verifySession).
 *   - When the session's `exp` passes, signs in again and comes back to the same page.
 *   - Everything is kept in memory only (no localStorage); the abnj_sso cookie is the
 *     single source of truth, and only the auth worker can read it.
 *
 * Other apps copy this file and remove verifyToken, login, the its-sign-in import and
 * the Token + DT check below (see src/sso/README.md).
 */

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const timerRef = useRef(null);
  const initialCheckRef = useRef(false);

  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);
  const [user, setUser] = useState(null);

  /** Signed in: remember who, and sign in again when the session expires (`exp`, unix seconds). */
  const setSignedIn = useCallback((exp, profile) => {
    setUser(profile);
    setIsAuthenticated(true);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => {
      setIsAuthenticated(false);
      setUser(null);
      abnjSso.signIn(); // back to this page after sign-in
    }, Math.max(exp * 1000 - Date.now(), 0));
  }, []);

  // On page load: is someone already signed in?
  useEffect(() => {
    if (initialCheckRef.current) return; // React StrictMode runs effects twice in development
    initialCheckRef.current = true;

    // Sign-in page with Token + DT from ITS: skip this check. verifyToken is about to
    // sign the user in, and the /callback page checks the session next.
    if (itsSignIn.readItsTokenAndDT(window.location.search)) {
      setAuthChecked(true);
      return;
    }

    abnjSso
      .verifySession() // null = not signed in
      .then((session) => session && setSignedIn(session.exp, session.user))
      .catch(() => {}) // auth worker unreachable: stay signed out
      .finally(() => setAuthChecked(true));
  }, [setSignedIn]);

  /**
   * Sign-in page only. Hands ITS's Token + DT to the auth worker (which verifies them and
   * sets the abnj_sso cookie).
   * @returns {{ success: true, redirectUrl: string } | false}  redirectUrl = the starting app's /callback
   */
  async function verifyToken(token, dt) {
    try {
      const result = await itsSignIn.verifyToken(token, dt); // null = not a valid ITS sign-in
      if (result) {
        setSignedIn(result.exp, result.user);
        return { success: true, redirectUrl: result.redirectUrl };
      }
    } catch (err) {
      console.error('Token verification failed:', err); // e.g. "App not registered"
    }
    return false;
  }

  return (
    <AuthContext.Provider value={{ isAuthenticated, authChecked, user, verifyToken, login: itsSignIn.startItsLogin }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}
